import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { api } from "@/lib/api";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ClientDashboard from "@/pages/client/ClientDashboard";
import ClientPage from "@/pages/client/ClientPage";
import Profile from "@/pages/Profile";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminClients from "@/pages/admin/AdminClients";
import AdminClientDetail from "@/pages/admin/AdminClientDetail";
import AdminPages from "@/pages/admin/AdminPages";

const Loader = () => (
  <div className="min-h-screen grid place-items-center bg-[#0B0F17]">
    <div className="h-8 w-8 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
  </div>
);

function Protected({ role, children }) {
  const { user, effectiveRole, isViewingAs, stopViewAs } = useAuth();
  const exiting = role === "admin" && isViewingAs;
  useEffect(() => {
    if (exiting) stopViewAs();
  }, [exiting, stopViewAs]);
  if (user === null || exiting) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (role && effectiveRole !== role) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function Home() {
  const { user, effectiveRole } = useAuth();
  if (user === null) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={effectiveRole === "admin" ? "/admin" : "/portale"} replace />;
}

function ViewAsEntry() {
  const { id } = useParams();
  const { user, startViewAs } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (user === null) return;
    if (!user || user.role !== "admin") return navigate("/", { replace: true });
    api.get(`/admin/clients/${id}`)
      .then((r) => { startViewAs(r.data); navigate("/portale", { replace: true }); })
      .catch(() => navigate("/admin/clienti", { replace: true }));
  }, [user, id, navigate, startViewAs]);
  return <Loader />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/portale" element={<Protected role="client"><ClientDashboard /></Protected>} />
          <Route path="/portale/pagina/:id" element={<Protected role="client"><ClientPage /></Protected>} />
          <Route path="/profilo" element={<Protected><Profile /></Protected>} />
          <Route path="/admin" element={<Protected role="admin"><AdminDashboard /></Protected>} />
          <Route path="/admin/clienti" element={<Protected role="admin"><AdminClients /></Protected>} />
          <Route path="/admin/clienti/:id" element={<Protected role="admin"><AdminClientDetail /></Protected>} />
          <Route path="/admin/clienti/:id/vista" element={<ViewAsEntry />} />
          <Route path="/admin/pagine" element={<Protected role="admin"><AdminPages /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster theme="dark" richColors position="top-right" />
    </AuthProvider>
  );
}

export default App;
